import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Button,
} from '@mui/material'

import { USER_PREFERENCES } from 'constants/settings'

import {
  MeasurementType,
  UnitSystem,
  TimeUnit,
  UserPreferencesUnitConfig,
} from 'app/MissionControlMethaneSolution/types/common'
import type { User } from 'types/user'
import { defaultValues } from 'app/MissionControlMethaneSolution/hooks/emissionEvent/useEmissionUnitsPreference'
import {
  TIME_UNIT_LABELS,
  UNIT_TEMPLATES,
  METHANE_DENSITY_OPTIONS,
  CONVERSION_FACTORS,
  DEFAULT_METHANE_DENSITY,
} from 'app/MissionControlMethaneSolution/constants/common'

type EmissionEventsUnitsProps = {
  onChange: (v: Partial<User>) => void
  user: Partial<User>
}

type FormValues = UserPreferencesUnitConfig

const styles = {
  label: {
    fontFamily: 'fontFamily',
    fontWeight: 'fontWeightRegular',
    fontSize: '0.875rem',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  value: {
    fontFamily: 'fontFamily',
    fontWeight: 'fontWeightRegular',
    fontSize: '1rem',
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
}

const EmissionEventsUnits = ({ onChange, user }: EmissionEventsUnitsProps) => {
  const [hasChanges, setHasChanges] = useState(false)

  const userPreferredEmissionUnits = useMemo(() => {
    return user?.preferences?.units?.emissionEvents
  }, [user])

  const [formValues, setFormValues] = useState<FormValues>(() => {
    return (userPreferredEmissionUnits as FormValues) || defaultValues
  })

  useEffect(() => {
    const syncUserPreferences = () => {
      if (userPreferredEmissionUnits) {
        setFormValues(userPreferredEmissionUnits as FormValues)
        setHasChanges(false)
      }
    }
    syncUserPreferences()
  }, [userPreferredEmissionUnits])

  const { username } = user

  const handleFieldChange = useCallback(
    (field: keyof FormValues, value: unknown) => {
      setFormValues(prevValues => {
        if (prevValues?.[field] === value) return prevValues

        const newFormValues = {
          ...prevValues,
          [field]: value,
        }

        setHasChanges(true)

        return newFormValues
      })
    },
    []
  )

  const handleSave = useCallback(() => {
    if (!username) return

    const newPayload = {
      username,
      preferences: {
        username,
        preference: USER_PREFERENCES.units,
        value: { emissionEvents: formValues },
      },
    }

    onChange?.(newPayload as unknown as Partial<User>)
    setHasChanges(false)
  }, [formValues, username, onChange])

  const formFields = useMemo(
    () => [
      {
        id: 'measurementType',
        label: 'Which Measurement Type would you prefer to see units in?',
        required: true,
        value: formValues.measurementType,
        options: [
          { value: MeasurementType.MASS, label: 'Mass' },
          { value: MeasurementType.VOLUME, label: 'Volume' },
        ],
      },
      {
        id: 'unitSystem',
        label: 'Which System would you prefer to see units in?',
        value: formValues.unitSystem,
        options: [
          { value: UnitSystem.METRIC, label: 'Metric' },
          { value: UnitSystem.IMPERIAL, label: 'Imperial' },
        ],
      },
      {
        id: 'timeUnit',
        label: 'Which Time Unit for Rate would you prefer to see units in?',
        value: formValues.timeUnit,
        options: [
          { value: TimeUnit.MINUTE, label: 'Minute' },
          { value: TimeUnit.HOUR, label: 'Hour' },
          { value: TimeUnit.DAY, label: 'Day' },
        ],
      },
    ],
    [formValues]
  )

  const methaneDensityOptions = useMemo(() => {
    const isImperial = formValues.unitSystem === UnitSystem.IMPERIAL
    const densityUnit = isImperial
      ? CONVERSION_FACTORS.DENSITY_UNITS.IMPERIAL
      : CONVERSION_FACTORS.DENSITY_UNITS.METRIC
    const conversionFactor = isImperial
      ? CONVERSION_FACTORS.UNIT_SYSTEM.KG_M3_TO_LB_FT3
      : 1

    return Object.values(METHANE_DENSITY_OPTIONS).map(option => {
      const displayDensity = option.density * conversionFactor

      const composedLabel = `${option.temperature} and ${option.pressure}`
      return {
        value: option.density, // Keep original metric value for storage
        label: `${option.organization}: Standard Conditions: ${composedLabel} – Methane Density: ${displayDensity} ${densityUnit}`,
      }
    })
  }, [formValues.unitSystem])

  const { measurementType, timeUnit, unitSystem } = formValues

  const displayUnits = useMemo(() => {
    const timeLabel = TIME_UNIT_LABELS[timeUnit || TimeUnit.HOUR]
    const unitTemplate = UNIT_TEMPLATES[measurementType][unitSystem]

    return {
      flowRate: `${unitTemplate.name} per ${timeLabel.long} (${unitTemplate.unit}/${timeLabel.short})`,
      totalQuantity: `${unitTemplate.name} (${unitTemplate.unit})`,
    }
  }, [measurementType, timeUnit, unitSystem])

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      component='form'
      noValidate
      autoComplete='off'
    >
      {formFields.map(field => (
        <FormControl key={field.id} component='fieldset'>
          <FormLabel component='legend' required={field.required}>
            {field.label}
          </FormLabel>
          <RadioGroup
            name={field.id}
            value={field.value}
            onChange={e => {
              handleFieldChange(field.id as keyof FormValues, e.target.value)
            }}
            row
          >
            {field.options.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      ))}

      {formValues.measurementType === MeasurementType.VOLUME && (
        <FormControl component='fieldset'>
          <FormLabel component='legend'>
            Which Conversion Standard would you like to use between Mass and
            Volume?
          </FormLabel>
          <RadioGroup
            name='methaneDensity'
            value={(
              formValues.methaneDensity || DEFAULT_METHANE_DENSITY
            ).toString()}
            onChange={e => {
              handleFieldChange('methaneDensity', Number(e.target.value))
            }}
          >
            {methaneDensityOptions.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value.toString()}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mt: 2,
          p: 2,
          borderTop: '1px solid #e0e0e0',
          borderRadius: 1,
        }}
      >
        <Box>
          <Typography gutterBottom sx={styles.label}>
            Flow Rate:
          </Typography>
          <Typography sx={styles.value}>{displayUnits.flowRate}</Typography>
        </Box>
        <Box>
          <Typography gutterBottom sx={styles.label}>
            Total Quantity:
          </Typography>
          <Typography sx={styles.value}>
            {displayUnits.totalQuantity}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  )
}

export default EmissionEventsUnits
